import { FC, useCallback, useEffect, useMemo } from "react";
import { Box, Button, Group, Space, Stack } from "@mantine/core";
import { IconPlaylist } from "@tabler/icons-react";
import { useIsLaptopOrBigger, useStyles } from "@/styles/useStyles";
import {
  useFilteredPlaylists,
  useRecommendedPlaylists,
} from "@/store/selectors/playlistSelectors";
import { PlaylistCard } from "@/components/playlists/PlaylistCard";
import { Navigation } from "@/services/Navigation";
import { Text } from "@/components/ui/Text";
import { useStore, useValueFromBreakpoint } from "@/hooks";
import { Analytics } from "@/services/Analytics";
import {
  useUserCompletedPlaylistKeys,
  useUserInProgressPlaylistKeys,
} from "@/store/selectors/userProfileSelectors";

import {
  FixedSizeList as _FixedSizeList,
  FixedSizeListProps,
} from "react-window";
import { AutoSizer as _AutoSizer, AutoSizerProps } from "react-virtualized";
import { useActiveUserPartner } from "@/store/selectors";
import { AdvizerSearchFilters } from "@/components/AdvizerSearchFilters";
import { AdvizerFilterButton } from "@/components/advizerSearchControls/AdvizerFilterButton";
import { AdvizerSearchInput } from "@/components/advizerSearchControls/AdvizerSearchInput";

const AutoSizer = _AutoSizer as unknown as FC<AutoSizerProps>;
const FixedSizeList = _FixedSizeList as unknown as FC<FixedSizeListProps>;

export function PlaylistLibrary() {
  const { playlists } = useFilteredPlaylists();
  const isLaptopOrBigger = useIsLaptopOrBigger();
  // const allPlaylists = useAllPlaylists();
  const headerHeight = useStyles().dimensions.headerHeight;
  const recommendedPlaylists = useRecommendedPlaylists();
  const inProgressPlaylistKeys = useUserInProgressPlaylistKeys();
  const completedPlaylistKeys = useUserCompletedPlaylistKeys();
  const activeUserPartner = useActiveUserPartner();
  const setIsInlineAdvizerSearchOpen = useStore(
    (state) => state.advizerSearch.setIsInlineAdvizerSearchOpen,
  );
  const { theme, ...styles } = useStyles();
  const collapseHeader = useStore(
    (state) => state.advizerSearch.collapseHeader,
  );
  const setCollapseHeader = useStore(
    (state) => state.advizerSearch.setCollapseHeader,
  );
  const isInlineAdvizerSearchOpen = useStore(
    (state) => state.advizerSearch.isInlineAdvizerSearchOpen,
  );

  const playlistGap = 32;

  useEffect(() => {
    setIsInlineAdvizerSearchOpen(true);
  }, [setIsInlineAdvizerSearchOpen]);

  const playlistCardHeight = useValueFromBreakpoint({
    mobile: 600,
    tablet: 410,
    laptop: 410,
    desktop: 410,
  });

  const maxPlaylistCardWidth = useValueFromBreakpoint({
    mobile: 500,
    tablet: 650,
    laptop: 700,
    desktop: 700,
  });

  const maxAdvizerListHeight = useValueFromBreakpoint({
    mobile: 120,
    tablet: 100,
    laptop: 100,
    desktop: 100,
  });

  const maxSpoilerHeight = useValueFromBreakpoint({
    mobile: 100,
    tablet: 80,
    laptop: 75,
    desktop: 75,
  });

  const advizerQuoteLineClamp = useValueFromBreakpoint({
    mobile: 4,
    tablet: 3,
    laptop: 2,
    desktop: 2,
  });

  const inProgressPlaylistKeyMap = useMemo(() => {
    return inProgressPlaylistKeys.reduce((acc: any, playlistId: string) => {
      acc[playlistId] = true;
      return acc;
    }, {});
  }, [inProgressPlaylistKeys]);

  const completedPlaylistKeyMap = useMemo(() => {
    return completedPlaylistKeys.reduce((acc: any, playlistId: string) => {
      acc[playlistId] = true;
      return acc;
    }, {});
  }, [completedPlaylistKeys]);

  const visiblePlaylists = useMemo(() => {
    return (
      playlists
        // .filter((playlist: any) => {
        //   const uniqIdStuff = Object.values(playlist.config?.uniqId || {})
        //     .map((arr: any) => (Array.isArray(arr) ? arr.join(" ") : ""))
        //     .join(" ");

        //   if (
        //     activeUserPartner &&
        //     playlist.config?.uniqId?.partners?.length &&
        //     !playlist.config?.uniqId?.partners?.includes(activeUserPartner.slug)
        //   ) {
        //     return false;
        //   }

        //   const textToSearch = `${playlist.id} ${playlist.title} ${playlist.description} ${uniqIdStuff}`;
        //   return textToSearch.toLowerCase().includes(search.toLowerCase());
        // })
        .sort((a, b) => {
          const aIsRecommended = recommendedPlaylists?.[a.id];
          const bIsRecommended = recommendedPlaylists?.[b.id];

          const aIsActiveUserPartnerPlaylist =
            a.config?.uniqId?.partners?.includes(activeUserPartner?.slug);
          const bIsActiveUserPartnerPlaylist =
            b.config?.uniqId?.partners?.includes(activeUserPartner?.slug);

          if (aIsActiveUserPartnerPlaylist && !bIsActiveUserPartnerPlaylist)
            return -1;
          if (!aIsActiveUserPartnerPlaylist && bIsActiveUserPartnerPlaylist)
            return 1;

          const aIsInProgress = inProgressPlaylistKeyMap[a.id];
          const bIsInProgress = inProgressPlaylistKeyMap[b.id];

          if (aIsInProgress && !bIsInProgress) return -1;
          if (!aIsInProgress && bIsInProgress) return 1;

          if (aIsRecommended && !bIsRecommended) return -1;
          if (!aIsRecommended && bIsRecommended) return 1;

          const aIsCompleted = completedPlaylistKeyMap[a.id];
          const bIsCompleted = completedPlaylistKeyMap[b.id];

          if (aIsCompleted && !bIsCompleted) return 1;
          if (!aIsCompleted && bIsCompleted) return -1;

          return 0;
        })
    );
  }, [
    activeUserPartner,
    playlists,
    completedPlaylistKeyMap,
    inProgressPlaylistKeyMap,
    recommendedPlaylists,
  ]);

  const rowRenderer = useCallback(
    ({ index, style }: { index: number; style: any }) => {
      const playlist = visiblePlaylists[index];
      return (
        <Stack
          w="100%"
          style={style}
          key={playlist.id}
          // h={playlistCardHeight}
          // maw={maxPlaylistCardWidth}
          align="center"
          pb={playlistGap}
        >
          <PlaylistCard
            playlistId={playlist.id}
            maxAdvizerListHeight={maxAdvizerListHeight}
            maxSpoilerHeight={maxSpoilerHeight}
            advizerQuoteLineClamp={advizerQuoteLineClamp}
            maxWidth={maxPlaylistCardWidth}
            height={playlistCardHeight}
            isPlaylistLibrary={true}
          />
        </Stack>
      );
    },
    [
      maxAdvizerListHeight,
      maxPlaylistCardWidth,
      playlistCardHeight,
      visiblePlaylists,
      maxSpoilerHeight,
      advizerQuoteLineClamp,
    ],
  );

  const renderListOfPlaylists = useCallback(() => {
    return (
      <Stack flex={1} w="100%" align="flex-start">
        <AutoSizer>
          {({ width, height }) => {
            return (
              <FixedSizeList
                width={width}
                height={height}
                itemCount={visiblePlaylists.length}
                itemSize={playlistCardHeight}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // paddingBottom: 1000
                  // width: "100%",
                }}
                onScroll={(props) => {
                  if (
                    props.scrollDirection === "forward" &&
                    props.scrollOffset > styles.dimensions.headerHeight
                  ) {
                    setCollapseHeader(true);
                  } else {
                    setCollapseHeader(false);
                  }
                }}
              >
                {rowRenderer}
              </FixedSizeList>
            );
          }}
        </AutoSizer>
      </Stack>
    );
  }, [
    visiblePlaylists.length,
    playlistCardHeight,
    rowRenderer,
    styles.dimensions.headerHeight,
    setCollapseHeader,
  ]);

  const TopSearchBar = useMemo(() => {
    return (
      <Stack
        w="100%"
        mx="auto"
        px="md"
        py="xs"
        align="center"
        pos="sticky"
        top={collapseHeader ? 0 : styles.dimensions.headerHeight}
        bg="white"
        style={{
          zIndex: 10,
          boxShadow: `1px 0 9px 0 ${theme.colors.slate[4]}`,
        }}

        // maw={1200}
        // style={{ borderBottom: `1px solid ${theme.colors.border[2]}` }}
      >
        <Group w="100%" justify="center" wrap="wrap">
          <Box pr="sm">
            <AdvizerFilterButton />
          </Box>

          {/* <TextInput
            id="playlistLibrarySearch"
            title="Search for playlists"
            value={search}
            // onChange={(e) => setSearch(e.target.value)}
            placeholder="Type to search for playlists..."
            size={isLaptopOrBigger ? "sm" : "sm"}
            radius="xl"
            leftSection={<IconSearch size={18} />}
            // miw={150}
            flex={1}
            onClear={() => {
              setSearch("");
            }}
            onDebouncedChange={(value) => {
              console.log({ value });
              setSearch(value);
            }}
          /> */}
          <Box flex={1} maw={300} miw={220}>
            <AdvizerSearchInput placeholder="Search for playlists..." />
          </Box>
          {isLaptopOrBigger && (
            <Text titleStyle="h4" fw={500} c="primary.9">
              {visiblePlaylists.length} playlist
              {visiblePlaylists.length > 1 ? "s" : ""}
            </Text>
          )}
          <Button
            radius="xl"
            size="sm"
            variant="filled"
            px="md"
            leftSection={<IconPlaylist size={18} />}
            color="primary"
            onClick={() => {
              Navigation.navToPlaylistAndStep({
                playlistId: "onboarding",
                step: "select-subjects",
              });
              Analytics.click({
                action: "Get Recommended Playlists from Playlists Library",
              });
            }}
          >
            {"Get Recommendations"}
          </Button>
        </Group>
      </Stack>
    );
  }, [
    collapseHeader,
    isLaptopOrBigger,
    styles.dimensions.headerHeight,
    theme.colors.slate,
    visiblePlaylists.length,
  ]);

  return (
    <Group
      // pt="xs"
      bg="background.1"
      h="100vh"
      w="100%"
      gap={0}
      style={{ overflow: "auto" }}

      // pt={collapseHeader ? 0 : styles.dimensions.headerHeight}

      // align="center"
    >
      {isLaptopOrBigger && isInlineAdvizerSearchOpen ? (
        <Stack
          h={`calc(100vh - ${
            collapseHeader ? 0 : styles.dimensions.headerHeight
          }px)`}
          maw={400}
          flex={1}
          pos="sticky"
          top={collapseHeader ? 0 : styles.dimensions.headerHeight - 10}
          style={{ zIndex: 10 }}
          gap={0}
          p={0}
          pt="xs"
        >
          <AdvizerSearchFilters hideCompanyFilter noTabTitles hideTabTitle />
        </Stack>
      ) : null}
      <Stack p={0} gap={0} flex={2} mah="100vh">
        {TopSearchBar}
        {!collapseHeader ? <Space h="md" /> : null}
        <Stack
          align="center"
          w="100%"
          mih={"100vh"}
          gap="lg"
          bg="background.1"
          pt={collapseHeader ? 0 : headerHeight * 1}
        >
          {renderListOfPlaylists()}
        </Stack>
        {/* <Group
        gap="xl"
        w="100%"
        wrap="wrap"
        justify="center"
        style={{ alignItems: "stretch" }} // Ensure all items have the same height
      > */}
        {/* {visiblePlaylists.map((playlist: any) => (
          <Stack key={playlist.id} w={600} maw="100%" justify="flex-start">
            <PlaylistCard playlistId={playlist.id} />
          </Stack>
        ))} */}
        {/* </Group> */}
      </Stack>
    </Group>
  );
}
